import {
  StyleSheet,
  TextStyle,
  View,
  TouchableOpacity,
  ViewStyle,
} from 'react-native';
import React, {useMemo, useEffect, useState} from 'react';
import colors from 'theme/Colors';
import {Text, Icon} from 'ui-kits';
import {corner_radius, spacing} from 'theme/Units';
import {Legend, LegendType} from 'components/common';
import ProgressCircle from 'components/common/circularProgress//ProgressCircle.web';
import {useSelector, useDispatch} from 'react-redux';
import {projectAnalyticsDashboard} from 'redux/selectors/projectAnalyticsSelect';
import {useToast, useAuthAccess} from 'hooks';
import {SUCCESS_KEY, ERROR_KEY} from 'constants/keys';

// routing related imports
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {RootNavigatorParams, Routes} from 'navigation/Routes';

import {
  ErrorCodes,
  getErrorMessage,
  getSuccessMessage,
  SuccessCodes,
} from 'utils/getMessages';
import {
  projectSuccess,
  projectFailure,
  selectedProjectID,
  selectedProjectData,
} from 'redux/selectors/projectSelector';
import {
  getProjectDetailsRequest,
  resetMessage,
} from 'redux/slices/projectSlice';

import {getFormattedDate} from 'utils/getFormattedDate';

type infoBoxProps = Readonly<{
  label: string;
  value: string;
  editable?: boolean;
}>;
function ProjectInfoBox({label, value, editable}: infoBoxProps) {
  return (
    <View style={styles.box}>
      <Text variant="sourcesans18" style={styles.labelValue}>
        {label}
      </Text>
      <Text variant="sourcesans18m" style={styles.labelValue}>
        {value}
      </Text>
    </View>
  );
}
function ReadMoreText({text}: Readonly<{text: string}>) {
  const [isExpanded, setIsExpanded] = useState(false);
  const words = text.split(' ');
  const shouldCollapse = words.length > 50;
  const displayText = isExpanded ? text : words.slice(0, 50).join(' ');

  return (
    <View>
      <Text>{displayText}</Text>
      {shouldCollapse && (
        <TouchableOpacity onPress={() => setIsExpanded(!isExpanded)}>
          <Text style={styles.readMore}>
            {isExpanded ? 'Read Less' : 'Read More'}
          </Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

export function ProjectDetails({
  navigation,
}: NativeStackScreenProps<RootNavigatorParams>) {
  const dispatch = useDispatch();
  const {showToasts} = useToast();
  // project will now also be able to edit project
  const {hasWriteAccess} = useAuthAccess();
  const getProjectSuccess = useSelector(projectSuccess);
  const getProjectFailure = useSelector(projectFailure);
  const dashboardStatus = useSelector(projectAnalyticsDashboard);
  const currentProjectID = useSelector(selectedProjectID);
  const projectData = useSelector(selectedProjectData);

  //error message
  useEffect(() => {
    if (getProjectFailure) {
      showToasts(
        ERROR_KEY,
        getErrorMessage(getProjectFailure as ErrorCodes),
        'error',
      );

      dispatch(resetMessage());
    }
  }, [dispatch, showToasts, getProjectFailure]);
  // success message toast
  useEffect(() => {
    if (getProjectSuccess) {
      if (['S-10016', 'S-10015'].includes(getProjectSuccess)) {
        showToasts(
          SUCCESS_KEY,
          getSuccessMessage(getProjectSuccess as SuccessCodes),
          'success',
        );
      }
      dispatch(resetMessage());
    }
  }, [dispatch, showToasts, getProjectSuccess]);

  useEffect(() => {
    // fetch requrest
    if (currentProjectID) {
      dispatch(getProjectDetailsRequest({id: currentProjectID}));
    }
  }, [currentProjectID, dispatch]);

  function handleEdit() {
    navigation.navigate(Routes.PROJECT_EDIT, {
      id: currentProjectID?.toString()!,
    });
  }

  const data: LegendType[] = useMemo<LegendType[]>(
    () => [
      {
        color: colors.palette.analytics10,
        label: 'Priority tasks',
        extras: dashboardStatus?.priority_task.toString(),
      },
      {
        color: colors.palette.analytics20,
        label: 'Pending tasks',
        extras: dashboardStatus?.pending_task.toString(),
      },
      {
        color: colors.palette.analytics30,
        label: 'Overdue tasks',
        extras: dashboardStatus?.pending_task.toString(),
      },
      {
        color: colors.palette.analytics40,
        label: 'Upcoming tasks',
        extras: dashboardStatus?.upcoming_task.toString(),
      },
    ],
    [dashboardStatus],
  );
  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.pid}>{projectData?.proj_id}</Text>

        <View style={styles.iconWrapper}>
          {hasWriteAccess ? (
            <View style={styles.icons}>
              <Icon type={'edit_white'} size={24} onPress={handleEdit} />
            </View>
          ) : null}
          <View style={styles.icons}>
            <Icon type={'settings'} size={32} />
          </View>
        </View>
      </View>

      <View style={styles.main}>
        <View style={styles.infoBox}>
          <ProjectInfoBox
            label="Sprint Duration"
            value={
              projectData?.sprint_duration
                ? projectData?.sprint_duration?.toString()! + ' days'
                : '0 days'
            }
            editable={true}
          />
          <ProjectInfoBox
            label="Starts On"
            value={projectData?.start_date ? getFormattedDate(new Date(projectData?.start_date)) : '_'}
          />
          <ProjectInfoBox
            label="Ends On"
            value={projectData?.end_date ? getFormattedDate(new Date(projectData?.end_date)) : '_'}
          />
        </View>
        <View style={styles.analytics}>
          <Legend
            data={data}
            fontSize={22}
            tileHeight={20}
            tileWidth={26}
            minWidth={300}
          />
          <ProgressCircle progressPercentage={25} size={200} strokeWidth={22} />
        </View>
        <View style={styles.description}>
          <Text variant="inter20" style={styles.title}>
            Description
          </Text>
          <Text>
            {projectData?.description && (
              <ReadMoreText text={projectData.description} />
            )}
          </Text>
        </View>
      </View>
    </View>
  );
}

type styleProps = {
  container: ViewStyle;
  header: ViewStyle;
  main: ViewStyle;
  box: ViewStyle;
  labelValue: TextStyle;
  pid: TextStyle;
  title: TextStyle;
  infoBox: ViewStyle;
  analytics: ViewStyle;
  description: ViewStyle;
  readMore: TextStyle;
  icons: ViewStyle;
  iconWrapper: ViewStyle;
};
const styles: styleProps = StyleSheet.create({
  icons: {
    width: 40,
    height: 40,
    backgroundColor: colors.palette.brandBlue,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: corner_radius['rounded-md'],
  },
  iconWrapper: {
    flexDirection: 'row',
    gap: spacing['3'],
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  readMore: {
    color: colors.palette.blueInfo,
    alignSelf: 'center',
  },
  container: {
    flex: 1,
    backgroundColor: colors.palette.white,
    padding: spacing['4'],
  },
  main: {
    flex: 1,
    flexDirection: 'column',
    gap: spacing['6'],
    alignItems: 'center',
    marginTop: spacing['2'],
  },
  box: {
    width: 150,
    height: 80,
    borderRadius: corner_radius['rounded-md'],
    borderWidth: 1.5,
    borderColor: colors.palette.brandBlue,
    justifyContent: 'center',
    alignItems: 'center',
  },
  labelValue: {
    color: colors.palette.brandBlue,
  },
  pid: {
    color: colors.palette.brandBlue,
    fontWeight: 500,
    fontSize: 24,
    margin: spacing['4'],
  },
  infoBox: {
    flexDirection: 'row',
    gap: spacing['6'],
  },
  analytics: {
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: spacing['16'],
  },
  description: {
    width: '90%',
    height: 'fit-content',
    gap: spacing['2'],
    paddingHorizontal: spacing['4'],
    paddingVertical: spacing['2'],
    borderRadius: corner_radius['rounded-md'],
    borderWidth: 1,
    borderColor: colors.palette.stroke,
    shadowOffset: {width: 0, height: 2},
    shadowRadius: 8,
    shadowColor: 'rgba(0,0,0,0.05)',
  },
  title: {
    fontWeight: 500,
    color: colors.palette.secondaryBlack5,
    fontSize: 20,
  },
});
